import { useMasterData } from '@nextgen-web-framework/all';
import { useMemo } from 'react';

/** Brand utility to distinguish between types that are just strings */
type Brand<K, T> = K & { __brand: T };

/** Branded role and group key types */
export type UserRole = Brand<string, 'UserRole'>;
export type UserGroupKey = Brand<string, 'UserGroupKey'>;

/** 🔧 Config schema */
export type Config = {
  roles: string[];
  groups: Record<string, string[]>;
};

/**  Hook to load dynamic or fallback roles/groups */
export const useRolesConfig = () => {
  const webShopConfigs = useMasterData<Config>('WebshopConfigs') as Config;

  const Roles = useMemo(() => {
    const roles = webShopConfigs?.roles;
    return roles.map((r) => r as UserRole);
  }, [webShopConfigs?.roles]);

  const Groups = useMemo(() => {
    const groups = webShopConfigs?.groups;
    return Object.fromEntries(
      Object.entries(groups).map(([key, values]) => [key as UserGroupKey, values.map((r) => r as UserRole)]),
    ) as Record<UserGroupKey, UserRole[]>;
  }, [webShopConfigs?.groups]);

  const UserRole = useMemo(() => {
    return Object.fromEntries(Roles.map((role) => [role, role])) as Record<string, UserRole>;
  }, [Roles]);

  /** Group keys – for reference in other files */
  const UserGroups = {
    AUTHENTICATED_USER: 'AuthenticatedUser' as UserGroupKey,
    // Add more if needed
  } as const;

  return { Roles, Groups, UserRole, UserGroups };
};
